<template>
  <div class="container">
    <div class="container01">
      <h1 class="container01_h1">销售开单</h1>

      <!-- 操作按钮 -->
      <el-row :gutter="20">
        <el-col :span="24" class="button-col">
          <div class="container01_button">
            <el-button @click="$router.back()">返回上一级</el-button>
            <el-button type="primary" @click="generateOrderNumber">生成单号</el-button>
            <el-button type="success" @click="saveOrder" :disabled="!canSave" :loading="saving">保存订单</el-button>
            <el-button type="warning" @click="printOrder" :disabled="!order.order_id">打印订单</el-button>
            <el-button @click="resetForm">重置</el-button>
          </div>
        </el-col>
      </el-row>
    </div>

    <!-- 订单基本信息 -->
    <div class="order-info">
      <el-card header="订单信息">
        <el-form :model="order" label-width="100px">
          <el-row :gutter="20">
            <el-col :span="8">
              <el-form-item label="订单编号" required>
                <el-input v-model="order.order_number" placeholder="点击生成单号按钮自动生成"></el-input>
              </el-form-item>
            </el-col>
            <el-col :span="8">
              <el-form-item label="开单日期" required>
                <el-date-picker v-model="order.order_time" type="datetime" placeholder="选择开单日期"
                  value-format="YYYY-MM-DD HH:mm:ss" style="width: 100%">
                </el-date-picker>
              </el-form-item>
            </el-col>
            <el-col :span="8">
              <el-form-item label="配送日期" required>
                <el-date-picker v-model="order.delivery_time" type="datetime" placeholder="选择配送日期"
                  value-format="YYYY-MM-DD HH:mm:ss" style="width: 100%">
                </el-date-picker>
              </el-form-item>
            </el-col>
          </el-row>

          <el-row :gutter="20">
            <el-col :span="12">
              <el-form-item label="配送客户" required>
                <el-select v-model="order.customer_id" filterable placeholder="请选择客户" style="width: 100%"
                  @change="handleCustomerChange">
                  <el-option v-for="customer in customers" :key="customer.customer_id" :label="customer.name"
                    :value="customer.customer_id">
                  </el-option>
                </el-select>
              </el-form-item>
            </el-col>
            <el-col :span="12">
              <el-form-item label="订单状态">
                <el-select v-model="order.status" style="width: 100%">
                  <el-option label="正常" value="正常"></el-option>
                  <el-option label="已完成" value="已完成"></el-option>
                  <el-option label="已取消" value="已取消"></el-option>
                </el-select>
              </el-form-item>
            </el-col>
          </el-row>
        </el-form>
      </el-card>
    </div>

    <!-- 商品明细 -->
    <div class="order-items">
      <el-card header="商品明细">
        <el-table :data="orderItems" border style="width: 100%">
          <el-table-column type="index" width="50" label="序号"></el-table-column>
          <el-table-column label="商品信息" min-width="200">
            <template #default="scope">
              <el-select 
                v-model="scope.row.merchandise_id" 
                filterable 
                placeholder="选择商品" 
                style="width: 100%"
                :ref="`merchandiseSelect_${scope.$index}`"
                @change="(value) => handleMerchandiseChange(value, scope.$index)"
                @keydown.enter="focusQuantityInput(scope.$index)">
                <el-option v-for="merchandise in merchandises" :key="merchandise.merchandise_id"
                  :label="`${merchandise.name} ${merchandise.specification || ''}`" :value="merchandise.merchandise_id">
                </el-option>
              </el-select>
            </template>
          </el-table-column>
          <el-table-column label="数量" width="200">
            <template #default="scope">
              <el-input-number 
                v-model="scope.row.quantity" 
                :min="0.01" 
                :step="1" 
                :precision="2"
                @change="calculateItemAmount(scope.$index)" 
                style="width: 100%"
                :ref="`quantityInput_${scope.$index}`"
                @keydown.enter="handleQuantityEnter(scope.$index)"
                @focus="handleQuantityFocus(scope.$index)">
              </el-input-number>
            </template>
          </el-table-column>
          <el-table-column label="单价" width="200">
            <template #default="scope">
              <el-input-number 
                v-model="scope.row.price" 
                :min="0" 
                :step="0.01" 
                :precision="2"
                @change="calculateItemAmount(scope.$index)" 
                style="width: 100%"
                :ref="`priceInput_${scope.$index}`"
                @keydown.enter="handlePriceEnter(scope.$index)"
                @focus="handlePriceFocus(scope.$index)">
              </el-input-number>
            </template>
          </el-table-column>
          <el-table-column label="金额" width="120">
            <template #default="scope">
              <span>{{ scope.row.amount.toFixed(2) }}</span>
            </template>
          </el-table-column>
          <el-table-column label="操作" width="80">
            <template #default="scope">
              <el-button type="danger" link @click="removeItem(scope.$index)" :disabled="orderItems.length <= 1">
                删除
              </el-button>
            </template>
          </el-table-column>
        </el-table>

        <div style="margin-top: 16px;">
          <el-button type="primary" @click="addItem">添加商品</el-button>
          <div style="float: right; text-align: right;">
            <div style="font-size: 16px; font-weight: bold;">
              合计金额: ¥ {{ totalAmount.toFixed(2) }}
            </div>
          </div>
        </div>
      </el-card>
    </div>

    <!-- 打印区域（隐藏） -->
    <div v-if="order.order_id" id="printArea" style="display: none;">
      <div class="print-header">
        <h2>销售订单</h2>
        <p>订单编号: {{ order.order_number }}</p>
      </div>
      <div class="print-info">
        <p>客户: {{ selectedCustomerName }}</p>
        <p>开单时间: {{ order.order_time }}</p>
        <p>配送时间: {{ order.delivery_time }}</p>
      </div>
      <table class="print-table" border="1" style="width: 100%; border-collapse: collapse;">
        <thead>
          <tr>
            <th>商品名称</th>
            <th>规格</th>
            <th>数量</th>
            <th>单价</th>
            <th>金额</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(item, index) in orderItems" :key="index">
            <td>{{ getMerchandiseName(item.merchandise_id) }}</td>
            <td>{{ getMerchandiseSpec(item.merchandise_id) }}</td>
            <td>{{ item.quantity }}</td>
            <td>{{ item.price.toFixed(2) }}</td>
            <td>{{ item.amount.toFixed(2) }}</td>
          </tr>
        </tbody>
        <tfoot>
          <tr>
            <td colspan="4" style="text-align: right;">合计:</td>
            <td>{{ totalAmount.toFixed(2) }}</td>
          </tr>
        </tfoot>
      </table>
    </div>
  </div>
</template>

<script>
import { ElMessage } from 'element-plus'
import ordersAPI from '@/api/orders'
import customersAPI from '@/api/customers'
import merchandiseAPI from '@/api/merchandises'

export default {
  name: 'CreateOrderView',
  data() {
    return {
      order: {
        order_number: '',
        customer_id: null,
        order_time: '',
        delivery_time: '',
        status: '正常',
        order_id: null,
        isEditMode: false,
        editingOrderId: null
      },
      orderItems: [
        {
          merchandise_id: null,
          quantity: 1,
          price: 0,
          amount: 0
        }
      ],
      customers: [],
      merchandises: [],
      loading: false,
      saving: false
    }
  },
  computed: {
    totalAmount() {
      return this.orderItems.reduce((sum, item) => sum + item.amount, 0)
    },
    canSave() {
      return this.order.order_number &&
        this.order.customer_id &&
        this.order.order_time &&
        this.order.delivery_time &&
        this.orderItems.every(item => item.merchandise_id && item.quantity > 0)
    },
    selectedCustomerName() {
      const customer = this.customers.find(c => c.customer_id === this.order.customer_id)
      return customer ? customer.name : ''
    }
  },
  created() {
    this.fetchCustomers()
    this.fetchMerchandises()
    this.setDefaultDates()
    const isEdit = this.$route.query.edit === 'true'
    const orderId = this.$route.query.order_id
    if (isEdit && orderId) {
      this.loadOrderData(parseInt(orderId))
    }
  },
  mounted() {
    // 页面加载后自动聚焦到第一个商品选择框
    this.$nextTick(() => {
      this.focusMerchandiseSelect(0)
    })
  },
  methods: {
    // 设置默认日期
    setDefaultDates() {
      const now = new Date()
      this.order.order_time = now.toISOString().slice(0, 19).replace('T', ' ')

      const tomorrow = new Date(now)
      tomorrow.setDate(tomorrow.getDate() + 1)
      this.order.delivery_time = tomorrow.toISOString().slice(0, 19).replace('T', ' ')
    },

    // 获取客户列表
    async fetchCustomers() {
      try {
        const response = await customersAPI.inquireCustomers({ per_page: 1000 })
        this.customers = response.customers || []
      } catch (error) {
        console.error('获取客户列表失败:', error)
        ElMessage.error('获取客户列表失败')
      }
    },

    // 获取商品列表
    async fetchMerchandises() {
      try {
        const response = await merchandiseAPI.inquireMerchandises({ per_page: 1000 })
        this.merchandises = response.merchandises || []
      } catch (error) {
        console.error('获取商品列表失败:', error)
        ElMessage.error('获取商品列表失败')
      }
    },

    // 生成订单编号
    async generateOrderNumber() {
      try {
        const response = await ordersAPI.generateOrderNumber()
        this.order.order_number = response.order_number
        ElMessage.success('订单编号生成成功')
      } catch (error) {
        console.error('生成订单编号失败:', error)
        ElMessage.error('生成订单编号失败')
      }
    },

    // 商品变更处理
    async handleMerchandiseChange(merchandiseId, index) {
      if (this.order.customer_id && merchandiseId) {
        // 优先获取历史价格
        await this.loadHistoricalPrice(this.order.customer_id, merchandiseId, index)
      }
      this.calculateItemAmount(index)
      
      // 自动跳转到数量输入框
      this.$nextTick(() => {
        this.focusQuantityInput(index)
      })
    },

    // 聚焦到商品选择框
    focusMerchandiseSelect(index) {
      const ref = `merchandiseSelect_${index}`
      if (this.$refs[ref] && this.$refs[ref].focus) {
        this.$refs[ref].focus()
      }
    },

    // 聚焦到数量输入框
    focusQuantityInput(index) {
      const ref = `quantityInput_${index}`
      if (this.$refs[ref] && this.$refs[ref].focus) {
        this.$refs[ref].focus()
        // 添加延迟确保聚焦完成后再选中文本
        this.$nextTick(() => {
          this.selectInputText(ref)
        })
      }
    },

    // 聚焦到单价输入框
    focusPriceInput(index) {
      const ref = `priceInput_${index}`
      if (this.$refs[ref] && this.$refs[ref].focus) {
        this.$refs[ref].focus()
        // 添加延迟确保聚焦完成后再选中文本
        this.$nextTick(() => {
          this.selectInputText(ref)
        })
      }
    },

    // 数量输入框获得焦点时的处理
    handleQuantityFocus(index) {
      const ref = `quantityInput_${index}`
      this.$nextTick(() => {
        this.selectInputText(ref)
      })
    },

    // 单价输入框获得焦点时的处理
    handlePriceFocus(index) {
      const ref = `priceInput_${index}`
      this.$nextTick(() => {
        this.selectInputText(ref)
      })
    },

    // 选中输入框文本的通用方法
    selectInputText(refName) {
      const inputComponent = this.$refs[refName]
      if (inputComponent && inputComponent.$el) {
        // 查找内部的input元素
        const inputEl = inputComponent.$el.querySelector('input')
        if (inputEl && inputEl.select) {
          inputEl.select()
        }
      }
    },

    // 数量输入框回车事件
    handleQuantityEnter(index) {
      // 自动跳转到单价输入框
      this.$nextTick(() => {
        this.focusPriceInput(index)
      })
    },

    // 单价输入框回车事件
    handlePriceEnter(index) {
      // 如果是最后一行，自动添加新行
      if (index === this.orderItems.length - 1) {
        this.addItem()
      } else {
        // 如果不是最后一行，跳转到下一行的商品选择框
        this.$nextTick(() => {
          this.focusMerchandiseSelect(index + 1)
        })
      }
    },

    // 客户变更处理
    handleCustomerChange(customerId) {
      // 当客户变更时，重新加载所有商品的历史价格
      this.orderItems.forEach(async (item, index) => {
        if (item.merchandise_id) {
          await this.loadHistoricalPrice(customerId, item.merchandise_id, index)
        }
      })
    },

    // 加载历史价格
    async loadHistoricalPrice(customerId, merchandiseId, index) {
      try {
        // 首先尝试获取历史价格
        const historicalPrice = await this.getHistoricalPrice(customerId, merchandiseId)
        if (historicalPrice !== null && historicalPrice !== undefined) {
          this.orderItems[index].price = historicalPrice
          ElMessage.success(`已自动匹配历史价格: ${historicalPrice}`)
        } else {
          // 如果没有历史价格，再获取客户特定价格
          await this.loadCustomerPrice(customerId, merchandiseId, index)
        }
      } catch (error) {
        console.error('加载价格失败:', error)
        // 如果获取历史价格失败，回退到客户特定价格
        await this.loadCustomerPrice(customerId, merchandiseId, index)
      }
    },

    // 获取历史价格
    async getHistoricalPrice(customerId, merchandiseId) {
      try {
        // 通过订单API获取客户商品的历史价格
        const response = await ordersAPI.getCustomerMerchandisePrice(customerId, merchandiseId)
        return response.price || null
      } catch (error) {
        console.error('获取历史价格失败:', error)
        return null
      }
    },

    // 加载客户特定价格
    async loadCustomerPrice(customerId, merchandiseId, index) {
      try {
        // 直接调用订单API获取客户商品价格
        const response = await ordersAPI.getCustomerMerchandisePrice(customerId, merchandiseId)
        if (response.price !== undefined && response.price !== null) {
          this.orderItems[index].price = response.price
          this.calculateItemAmount(index)
        } else {
          // 如果没有历史价格，则设置为0或默认价格
          this.orderItems[index].price = 0
          this.calculateItemAmount(index)
        }
      } catch (error) {
        console.error('获取价格失败:', error)
        this.orderItems[index].price = 0
        this.calculateItemAmount(index)
      }
    },

    // 计算商品金额
    calculateItemAmount(index) {
      const item = this.orderItems[index]
      item.amount = item.quantity * item.price
    },

    // 添加商品行
    addItem() {
      this.orderItems.push({
        merchandise_id: null,
        quantity: 1,
        price: 0,
        amount: 0
      })
      
      // 自动聚焦到新添加的商品选择框
      this.$nextTick(() => {
        const newIndex = this.orderItems.length - 1
        this.focusMerchandiseSelect(newIndex)
      })
    },

    // 删除商品行
    removeItem(index) {
      if (this.orderItems.length > 1) {
        this.orderItems.splice(index, 1)
        
        // 删除后聚焦到上一行的单价输入框，如果是第一行则聚焦到商品选择框
        this.$nextTick(() => {
          if (index > 0) {
            this.focusPriceInput(index - 1)
          } else {
            this.focusMerchandiseSelect(0)
          }
        })
      }
    },

    // 保存订单
    async saveOrder() {
      try {
        this.saving = true

        const orderData = {
          order: {
            order_number: this.order.order_number,
            customer_id: this.order.customer_id,
            order_time: this.order.order_time,
            delivery_time: this.order.delivery_time,
            status: this.order.status
          },
          items: this.orderItems.map(item => ({
            merchandise_id: item.merchandise_id,
            quantity: item.quantity,
            price: item.price
          }))
        }

        let response
        if (this.isEditMode) {
          response = await ordersAPI.updateOrder(this.editingOrderId, orderData)
          this.order.order_id = this.editingOrderId
        } else {
          response = await ordersAPI.createOrder(orderData)
          this.order.order_id = response.order_id
        }

        ElMessage.success(this.isEditMode ? '订单更新成功' : '订单保存成功')
      } catch (error) {
        console.error('保存订单失败:', error)
        ElMessage.error(error.response?.data?.message || '保存订单失败')
      } finally {
        this.saving = false
      }
    },

    // 添加加载订单数据的方法（用于编辑）
    async loadOrderData(orderId) {
      try {
        this.loading = true
        const response = await ordersAPI.inquireOrder(orderId)

        // 填充订单基本信息
        this.order = {
          order_number: response.order_number,
          customer_id: response.customer_id,
          order_time: response.order_time,
          delivery_time: response.delivery_time,
          status: response.status,
          order_id: response.order_id
        }

        // 填充订单明细
        this.orderItems = response.items.map(item => ({
          merchandise_id: item.merchandise_id,
          quantity: item.quantity,
          price: item.price,
          amount: item.amount
        }))

        this.isEditMode = true
        this.editingOrderId = orderId

      } catch (error) {
        console.error('加载订单数据失败:', error)
        ElMessage.error('加载订单数据失败')
      } finally {
        this.loading = false
      }
    },

    // 打印订单
    printOrder() {
      const printContent = document.getElementById('printArea').innerHTML
      const originalTitle = document.title
      document.title = '销售订单-' + this.order.order_number

      const printWindow = window.open('', '_blank')
      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
          <head>
            <title>${document.title}</title>
            <style>
              body { 
                font-family: 'SimSun', serif; 
                margin: 20px;
                font-size: 14px;
              }
              .print-header {
                text-align: center;
                margin-bottom: 20px;
                border-bottom: 2px solid #000;
                padding-bottom: 10px;
              }
              .print-header h2 {
                margin: 0;
                font-size: 24px;
              }
              .print-info p {
                margin: 5px 0;
                display: inline-block;
                margin-right: 30px;
              }
              .print-table {
                margin-top: 20px;
                font-size: 14px;
                width: 100%;
                border-collapse: collapse;
              }
              .print-table th,
              .print-table td {
                padding: 8px;
                text-align: center;
                border: 1px solid #000;
              }
              .print-table th {
                background-color: #f5f5f5;
                font-weight: bold;
              }
              @media print {
                body { margin: 0; }
              }
            </style>
          </head>
          <body>${printContent}</body>
        </html>
      `)
      printWindow.document.close()

      printWindow.onload = () => {
        printWindow.focus()
        printWindow.print()
        printWindow.onafterprint = () => {
          printWindow.close()
          document.title = originalTitle
        }
      }
    },

    // 重置表单
    resetForm() {
      this.order = {
        order_number: '',
        customer_id: null,
        order_time: '',
        delivery_time: '',
        status: '正常',
        order_id: null
      }
      this.orderItems = [{
        merchandise_id: null,
        quantity: 1,
        price: 0,
        amount: 0
      }]
      this.setDefaultDates()
      
      // 重置后聚焦到第一个商品选择框
      this.$nextTick(() => {
        this.focusMerchandiseSelect(0)
      })
    },

    // 获取商品名称（用于打印）
    getMerchandiseName(merchandiseId) {
      const merchandise = this.merchandises.find(m => m.merchandise_id === merchandiseId)
      return merchandise ? merchandise.name : ''
    },

    // 获取商品规格（用于打印）
    getMerchandiseSpec(merchandiseId) {
      const merchandise = this.merchandises.find(m => m.merchandise_id === merchandiseId)
      return merchandise ? merchandise.specification : ''
    }
  }
}
</script>

<style scoped>
.container {
  padding: 20px;
}

.container01_h1 {
  text-align: center;
  margin-bottom: 20px;
}

.container01_button {
  text-align: center;
  margin-bottom: 20px;
}

.order-info,
.order-items {
  margin-bottom: 20px;
}
</style>